import { useState } from 'react';
import type { ReactNode } from 'react';
import { DataGrid } from '@/components/tables/DataGrid';
import type { DataGridColumn } from '@/components/tables/DataGrid';
import { RowActionButtons } from '@/components/tables/RowActionButtons';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { Select2 } from '@/components/forms/Select2';
import type { Select2Option } from '@/components/forms/Select2';
import { globalSymbolsApi } from '@/api/services/globalSymbols';
import { SPREAD_BET } from '@/api/types/globalSymbols';
import type { GlobalSymbolDto } from '@/api/types/globalSymbols';
import { ApiError } from '@/api/client/abpError';
import { GlobalSymbolFormModal } from './GlobalSymbolFormModal';
import type { GlobalSymbolFormValues } from './GlobalSymbolFormModal';
import { GlobalSymbolsPermissions } from './permissions';
import { useCreateGlobalSymbol, useFormData, useUpdateGlobalSymbol } from './api';

// Add is inline (legacy adds a new editable row, not a modal); edit still uses the modal.
type ModalState = { mode: 'edit'; symbol: GlobalSymbolDto } | null;

function toInt(value: string): number {
  if (value === '') return 0;
  const n = parseInt(value.replace(/,/g, ''), 10);
  return Number.isNaN(n) ? 0 : n;
}

const EMPTY_DRAFT: GlobalSymbolFormValues = {
  symbol: '',
  typeName: '',
  sbUnderlyingCurrency: '',
  assetClass: '',
  baseCurrency: '',
  quoteCurrency: '',
  quoteAssetClass: '',
  countryName: '',
  contractSize: '',
  digits: '',
};

export function GlobalSymbolsTab() {
  const can = usePermission();
  const canCreate = can(GlobalSymbolsPermissions.create);
  const canEdit = can(GlobalSymbolsPermissions.edit);

  const toast = useToast();
  const formDataQuery = useFormData();
  const createMut = useCreateGlobalSymbol();
  const updateMut = useUpdateGlobalSymbol();

  const [modal, setModal] = useState<ModalState>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<GlobalSymbolFormValues>(EMPTY_DRAFT);

  const table = useServerDataTable<GlobalSymbolDto>({
    queryKey: ['globalSymbols', 'list'],
    fetcher: (req) => globalSymbolsApi.getListForDataTable(req),
  });

  const columns: DataGridColumn<GlobalSymbolDto>[] = [
    { key: 'symbol', header: 'Instrument', sortable: true, cell: (r) => r.symbol },
    { key: 'typeName', header: 'Instrument type', sortable: true, cell: (r) => r.typeName },
    {
      key: 'sbUnderlyingCurrency',
      header: 'SB underlying currency',
      sortable: true,
      cell: (r) => r.sbUnderlyingCurrency,
    },
    { key: 'assetClass', header: 'Asset Class', sortable: true, cell: (r) => r.assetClass },
    { key: 'baseCurrency', header: 'Base', sortable: true, cell: (r) => r.baseCurrency },
    { key: 'quoteCurrency', header: 'Quote', sortable: true, cell: (r) => r.quoteCurrency },
    {
      key: 'quoteAssetClass',
      header: 'Quote Asset Class',
      sortable: true,
      cell: (r) => r.quoteAssetClass,
    },
    { key: 'countryName', header: 'Country', sortable: true, cell: (r) => r.countryName },
    { key: 'contractSize', header: 'Contract Size', sortable: true, cell: (r) => r.contractSize },
    { key: 'digits', header: 'Digits', sortable: true, cell: (r) => r.digits },
  ];

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function buildPayload(values: GlobalSymbolFormValues) {
    const typeId =
      formDataQuery.data?.symbolTypeList.find((t) => t.name === values.typeName)?.id ?? 0;
    return {
      typeId,
      sbUnderlyingCurrency: values.sbUnderlyingCurrency || '',
      assetClass: values.assetClass || '',
      baseCurrency: values.baseCurrency || '',
      quoteCurrency: values.quoteCurrency || '',
      quoteAssetClass: values.quoteAssetClass || '',
      countryName: values.countryName || '',
      contractSize: toInt(values.contractSize),
      digits: toInt(values.digits),
    };
  }

  // Edit still uses the modal.
  function handleSubmit(values: GlobalSymbolFormValues) {
    if (!modal || !formDataQuery.data) return;
    updateMut.mutate(
      { id: modal.symbol.id, input: buildPayload(values) },
      {
        onSuccess: () => {
          toast.success('Global symbol updated.');
          setModal(null);
        },
        onError: (err) => reportError(err, 'Failed to update the global symbol.'),
      },
    );
  }

  // Add is an inline editable row (legacy behaviour), pre-filled like legacy's new row.
  function startAdd() {
    const d = formDataQuery.data;
    const fx = d?.assetClassList.includes('FX-CU') ? 'FX-CU' : d?.assetClassList[0] ?? '';
    setDraft({
      ...EMPTY_DRAFT,
      typeName: d?.symbolTypeList[0]?.name ?? '',
      assetClass: fx,
      quoteAssetClass: fx,
      countryName: d?.countryList[0] ?? '',
    });
    setAdding(true);
  }

  function setField(key: keyof GlobalSymbolFormValues, value: string) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function saveAdd() {
    if (!formDataQuery.data) return;
    const required: [boolean, string][] = [
      [!draft.symbol.trim(), 'Symbol is required.'],
      [!draft.typeName.trim(), 'Instrument type is required.'],
      [!draft.assetClass.trim(), 'Asset Class is required.'],
      [!draft.baseCurrency.trim(), 'Base is required.'],
      [!draft.quoteCurrency.trim(), 'Quote is required.'],
      [!draft.quoteAssetClass.trim(), 'Quote Asset Class is required.'],
      [!(Number(draft.contractSize.replace(/,/g, '')) > 0), 'Contract Size is required.'],
    ];
    const firstError = required.find(([bad]) => bad);
    if (firstError) {
      toast.error(firstError[1]);
      return;
    }
    createMut.mutate(
      { symbol: draft.symbol.trim(), ...buildPayload(draft) },
      {
        onSuccess: () => {
          toast.success('Global symbol created.');
          setAdding(false);
        },
        onError: (err) => reportError(err, 'Failed to create the global symbol.'),
      },
    );
  }

  if (formDataQuery.isLoading) return <Spinner />;
  if (formDataQuery.isError || !formDataQuery.data)
    return (
      <ErrorState message="Could not load global symbol form data." onRetry={() => formDataQuery.refetch()} />
    );

  const data = formDataQuery.data;
  const typeOptions: Select2Option[] = data.symbolTypeList.map((t) => ({
    value: t.name,
    label: t.name,
  }));
  const assetClassOptions: Select2Option[] = data.assetClassList.map((a) => ({ value: a, label: a }));
  const countryOptions: Select2Option[] = data.countryList.map((c) => ({ value: c, label: c }));

  const textCell = (key: keyof GlobalSymbolFormValues, disabled = false) => (
    <input
      className="form-control form-control-sm"
      value={draft[key]}
      disabled={disabled}
      autoComplete="off"
      onChange={(e) => setField(key, e.target.value)}
    />
  );
  // Every dropdown is a Select2 (project convention). dropdownParent = body so the
  // dropdown isn't clipped by the table's horizontal scroll container.
  const selectCell = (key: keyof GlobalSymbolFormValues, options: Select2Option[]) => (
    <Select2
      value={draft[key]}
      allowClear={false}
      searchable
      onChange={(v) => setField(key, v ?? '')}
      options={options}
      dropdownParent={document.body}
    />
  );

  // Inline add row (legacy behaviour): editable cells keyed by column.
  const createRow: Record<string, ReactNode> | undefined = adding
    ? {
        symbol: textCell('symbol'),
        typeName: selectCell('typeName', typeOptions),
        sbUnderlyingCurrency: textCell('sbUnderlyingCurrency', draft.typeName !== SPREAD_BET),
        assetClass: selectCell('assetClass', assetClassOptions),
        baseCurrency: textCell('baseCurrency'),
        quoteCurrency: textCell('quoteCurrency'),
        quoteAssetClass: selectCell('quoteAssetClass', assetClassOptions),
        countryName: selectCell('countryName', countryOptions),
        contractSize: textCell('contractSize'),
        digits: textCell('digits'),
      }
    : undefined;

  return (
    <div className="card border-0 shadow-none">
      <div className="card-body">
        <DataGrid
          controller={table}
          columns={columns}
          rowKey={(r) => r.id}
          emptyMessage="No global symbols found."
          toolbarEnd={
            canCreate && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={startAdd}
                disabled={adding}
              >
                <i className="fa fa-plus me-1" />
                Add New Global Symbol
              </button>
            )
          }
          createRow={createRow}
          createRowActions={
            adding ? (
              <div className="d-inline-flex gap-1 justify-content-center">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success"
                  title="Save"
                  onClick={saveAdd}
                  disabled={createMut.isPending}
                >
                  <i className="fa fa-save" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  title="Cancel"
                  onClick={() => setAdding(false)}
                  disabled={createMut.isPending}
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            ) : undefined
          }
          rowActions={
            canEdit || canCreate
              ? (row) =>
                  canEdit ? (
                    <RowActionButtons onEdit={() => setModal({ mode: 'edit', symbol: row })} />
                  ) : null
              : undefined
          }
        />
      </div>

      {modal && (
        <GlobalSymbolFormModal
          show
          mode={modal.mode}
          formData={formDataQuery.data}
          initial={
            modal.mode === 'edit'
              ? {
                  symbol: modal.symbol.symbol,
                  typeName: modal.symbol.typeName,
                  sbUnderlyingCurrency: modal.symbol.sbUnderlyingCurrency,
                  assetClass: modal.symbol.assetClass,
                  baseCurrency: modal.symbol.baseCurrency,
                  quoteCurrency: modal.symbol.quoteCurrency,
                  quoteAssetClass: modal.symbol.quoteAssetClass,
                  countryName: modal.symbol.countryName,
                  contractSize: String(modal.symbol.contractSize),
                  digits: String(modal.symbol.digits),
                }
              : undefined
          }
          busy={createMut.isPending || updateMut.isPending}
          onSubmit={handleSubmit}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
